import React, { FunctionComponent } from "react";
import { useLocation } from "react-router-dom";
import { Box, Divider, List } from "@mui/material";

import { EsgBlackSmall } from "assets";
import {
  capitalizeString,
  getLoggedUserData,
  isFeatureEnabled,
  isPageEnabled,
} from "utils";
import {
  DOCUMENT_UPLOAD_PAGE,
  SITES_PAGE,
  JOBS_PAGE,
  EMISSION_FACTORS_PAGE,
  SITES_EMISSIONS_PAGE,
  AUDIT_LOGS_PAGE,
  SETTINGS_PAGE,
  SCORECARD_PAGE,
  DASHBOARD_PAGE,
  ACCELERATE_MENU,
} from "pages";

import { useGetConfigParameter, useAmplitude } from "hooks";
import { Drawer, MAX_DRAWER_WIDTH, COLORS } from "components";
import {
  Esg12Label,
  Label18SemiBold,
  PrimaryLabel,
} from "components/typography";
import { USER_ROLES, USER_ROLES_NAMES } from "recoils";

import { SidebarToggleButton, SidebarItem } from "./components";
import { MenuItemsProps, SidebarProps } from "./types";
import "./sideBar.css";

export const Sidebar: FunctionComponent<SidebarProps> = ({
  isOpen,
  open,
  close,
}) => {
  const { pathname } = useLocation();
  const { trackEvent } = useAmplitude();
  const { data: configParameter } = useGetConfigParameter();
  const { firstName, lastName, roles } = getLoggedUserData();

  const absolutePathPrefix = `/${pathname.split("/")[1]}`;

  const isAccelerateMenuEnabled =
    isFeatureEnabled(configParameter, "target_setting_feature") ||
    isFeatureEnabled(configParameter, "initiative_managament_feature") ||
    isFeatureEnabled(configParameter, "initiative_modeling_feature");

  const allMenuItems: MenuItemsProps[] = [
    SITES_PAGE,
    SCORECARD_PAGE,
    DOCUMENT_UPLOAD_PAGE,
    SITES_EMISSIONS_PAGE,
    DASHBOARD_PAGE,
    { ...ACCELERATE_MENU, isEnabled: isAccelerateMenuEnabled },
    EMISSION_FACTORS_PAGE,
    AUDIT_LOGS_PAGE,
    JOBS_PAGE,
  ];

  // Two behaviours when a feature is disabled:
  // - Show in sidebar (disabled + tooltip): e.g. Scorecard. Use premiumFeatureTooltipInfoMessage.
  // - Hide from sidebar: e.g. Targets. Set hideInSidebarWhenDisabled: true on the page const.
  const itemsWithEnabled = allMenuItems.map((page) => ({
    page,
    isEnabled: isPageEnabled(page, configParameter),
  }));

  const visibleMenuItems = itemsWithEnabled.filter(
    ({ page, isEnabled }) => isEnabled || !page.hideInSidebarWhenDisabled,
  );

  return (
    <Drawer
      variant="permanent"
      open={isOpen}
      onClose={close}
      collapsedWidth={101}
      expandedWidth={MAX_DRAWER_WIDTH}
      sx={{
        "& .MuiDrawer-paper": {
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          boxSizing: "border-box", // Ensure consistency
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          scrollbarWidth: "none",
        }}
      >
        <Box sx={{ display: isOpen ? "flex" : undefined }}>
          <Box
            sx={{
              minHeight: 64,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pl: isOpen ? "28px" : undefined,
              svg: {
                position: "relative",
                left: "2px",
              },
            }}
          >
            {isOpen ? (
              <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                <EsgBlackSmall fill={COLORS.Romance} />
                <Box>
                  <Label18SemiBold sx={{ color: COLORS.Romance }}>
                    {capitalizeString(firstName)} {capitalizeString(lastName)}
                  </Label18SemiBold>
                  <PrimaryLabel>
                    {roles?.includes("ADMIN") || roles?.includes("SUPER_ADMIN")
                      ? USER_ROLES_NAMES[USER_ROLES.MODULE_ADMIN]
                      : USER_ROLES_NAMES[roles[0]]}
                  </PrimaryLabel>
                </Box>
              </Box>
            ) : (
              <EsgBlackSmall />
            )}
          </Box>
        </Box>
        {isOpen && <Divider />}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <>
            <Box
              sx={{
                pl: isOpen ? 4 : 0,
                pt: isOpen ? 2 : 0,
                justifyContent: isOpen ? "left" : "center",
                textAlign: !isOpen ? "center" : undefined,
              }}
            >
              <Esg12Label>Scope 1 & 2</Esg12Label>
            </Box>
            <List
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: isOpen ? "flex-start" : "center",
                marginLeft: isOpen ? 2 : 0,
                pt: 2,
                px: 1,
                justifyContent: "flex-start",
              }}
            >
              {visibleMenuItems.map(({ page, isEnabled }) => (
                <SidebarItem
                  key={page.ABSOLUTE_PATH}
                  isOpen={isOpen}
                  icon={page.ICON}
                  title={page.TITLE}
                  path={page.ABSOLUTE_PATH}
                  isSelected={page.ABSOLUTE_PATH === absolutePathPrefix}
                  renderMenu={page.renderMenu}
                  trackEventFn={trackEvent}
                  sidebarTitle={page.SIDEBAR_TITLE ?? page.TITLE}
                  isEnabled={isEnabled}
                  premiumFeatureTooltipInfoMessage={
                    page.premiumFeatureTooltipInfoMessage
                  }
                />
              ))}
            </List>
          </>
        </Box>
        <Divider sx={{ mx: 2, my: 1 }} />
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            alignItems: isOpen ? "flex-start" : "center",
            marginLeft: isOpen ? 2 : 0,
            justifyContent: "flex-start",
          }}
        >
          {[SETTINGS_PAGE].map((page) => (
            <SidebarItem
              key={page.ABSOLUTE_PATH}
              isOpen={isOpen}
              icon={page.ICON}
              title={page.TITLE}
              path={page.ABSOLUTE_PATH}
              isSelected={page.ABSOLUTE_PATH === absolutePathPrefix}
              renderMenu={page.renderMenu}
              trackEventFn={trackEvent}
              sidebarTitle={page.TITLE}
            />
          ))}
        </List>
      </Box>
      <SidebarToggleButton
        isOpen={isOpen}
        close={close}
        open={open}
        collapsedWidth={101}
        expandedWidth={MAX_DRAWER_WIDTH}
      />
    </Drawer>
  );
};
